import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FolderKanban, Clock, AlertTriangle, CheckCircle, 
  IndianRupee, Calendar, Send, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const FreelancerPortalPage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [committedDate, setCommittedDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === 'projects') {
        const res = await api.get('/freelancer/my-projects');
        setProjects(res.data);
      } else {
        const res = await api.get('/freelancer/my-payments');
        setPayments(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!committedDate) {
      toast.error('Please select a delivery date');
      return;
    }
    try {
      await api.post(`/freelancer-assignments/${selectedAssignment.id}/confirm`, {
        committed_delivery_date: new Date(committedDate).toISOString()
      });
      toast.success('Assignment confirmed!');
      setShowConfirmDialog(false);
      setCommittedDate('');
      setSelectedAssignment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  const handleMarkDelivered = async (assignmentId) => {
    try {
      await api.patch(`/freelancer-assignments/${assignmentId}/status`, {
        status: 'delivered'
      });
      toast.success('Marked as delivered!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleStartWork = async (assignmentId) => {
    try {
      await api.patch(`/freelancer-assignments/${assignmentId}/status`, {
        status: 'in_progress'
      });
      toast.success('Started working!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_confirmation: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      confirmed: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      in_progress: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      delivered: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      late: 'bg-rose-500/10 text-rose-400 border-rose-500/20'
    };
    return badges[status] || badges.pending_confirmation;
  };

  const getTrackingBadge = (status) => {
    const badges = {
      on_track: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      at_risk: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      late: 'bg-rose-500/10 text-rose-400 border-rose-500/20'
    };
    return badges[status] || badges.on_track;
  };

  const pendingCount = projects.filter(p => p.needs_confirmation).length;
  const activeCount = projects.filter(p => ['confirmed', 'in_progress'].includes(p.status)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="freelancer-portal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">My Projects</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Welcome, {user?.name}. {pendingCount > 0 && <span className="text-amber-400">{pendingCount} assignment(s) need confirmation.</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}
          className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-freelancer-portal">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-white">{projects.length}</p>
            <p className="text-xs text-zinc-500 mt-1">Total Assigned</p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-amber-400">{pendingCount}</p>
            <p className="text-xs text-zinc-500 mt-1">Pending Confirm</p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-purple-400">{activeCount}</p>
            <p className="text-xs text-zinc-500 mt-1">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-emerald-400">
              {payments ? formatCurrency(payments.total_received) : '-'}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Total Received</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#121212] border border-white/10 p-1">
          <TabsTrigger 
            value="projects" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="portal-projects-tab"
          >
            Projects ({projects.length})
          </TabsTrigger>
          <TabsTrigger 
            value="payments" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="portal-payments-tab"
          >
            Payments
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div className="space-y-4" data-testid="portal-projects-list">
          {projects.length === 0 && (
            <div className="text-center py-16 text-zinc-500">
              <FolderKanban className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No projects assigned to you yet</p>
            </div>
          )}
          {projects.map(project => (
            <Card key={project.id} className="bg-[#121212] border-white/10" data-testid={`portal-project-${project.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-medium">{project.project_title}</h3>
                      <Badge variant="outline" className={getStatusBadge(project.status)}>
                        {project.status.replace(/_/g, ' ')}
                      </Badge>
                      {project.tracking_status !== 'on_track' && project.status !== 'delivered' && (
                        <Badge variant="outline" className={getTrackingBadge(project.tracking_status)}>
                          {project.tracking_status === 'at_risk' ? 'At Risk' : 'Late'}
                        </Badge>
                      )}
                    </div>
                    {project.event_type && (
                      <p className="text-xs text-zinc-500 mb-3">{project.event_type}</p>
                    )}
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Raw Files</p>
                        <p className="text-zinc-300 mt-0.5 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(project.raw_files_handover_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">My Committed Date</p>
                        <p className="text-zinc-300 mt-0.5 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(project.committed_delivery_date)}
                        </p>
                      </div>
                      {project.days_remaining !== null && project.status !== 'delivered' && (
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Days Left</p>
                          <p className={`mt-0.5 font-medium ${project.days_remaining < 0 ? 'text-rose-400' : project.days_remaining <= 3 ? 'text-yellow-400' : 'text-emerald-400'}`}>
                            {project.days_remaining < 0 ? `${Math.abs(project.days_remaining)} days late` : `${project.days_remaining} days`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                    {project.needs_confirmation && (
                      <Button
                        onClick={() => {
                          setSelectedAssignment(project);
                          setShowConfirmDialog(true);
                        }}
                        className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                        data-testid={`confirm-btn-${project.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" /> Confirm
                      </Button>
                    )}
                    {project.status === 'confirmed' && (
                      <Button
                        onClick={() => handleStartWork(project.id)}
                        variant="outline"
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                        data-testid={`start-work-btn-${project.id}`}
                      >
                        <Send className="h-4 w-4 mr-2" /> Start Work
                      </Button>
                    )}
                    {project.status === 'in_progress' && (
                      <Button
                        onClick={() => handleMarkDelivered(project.id)}
                        variant="outline"
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        data-testid={`deliver-btn-${project.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" /> Mark Delivered
                      </Button>
                    )}
                    {project.status === 'delivered' && (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 px-3 py-1.5">
                        Delivered
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-4" data-testid="portal-payments-section">
          {payments && (
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-1">
                  <IndianRupee className="h-5 w-5 text-[#F5A623]" />
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Received</p>
                </div>
                <p className="text-3xl font-semibold text-[#F5A623]">{formatCurrency(payments.total_received)}</p>
              </CardContent>
            </Card>
          )}

          {payments?.payments?.length > 0 ? (
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Project</th>
                        <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Amount</th>
                        <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.payments.map(payment => (
                        <tr key={payment.id} className="border-b border-white/5">
                          <td className="p-4 text-white">{payment.project_title}</td>
                          <td className="p-4 text-emerald-400 font-medium">{formatCurrency(payment.amount)}</td>
                          <td className="p-4 text-zinc-400">{formatDate(payment.payment_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              <IndianRupee className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No payments recorded yet</p>
            </div>
          )}
        </div>
      )}

      {/* Confirm Assignment Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Assignment</DialogTitle>
          </DialogHeader>
          {selectedAssignment && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#0A0A0A] rounded border border-white/5">
                <p className="text-white font-medium">{selectedAssignment.project_title}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Raw files by: {formatDate(selectedAssignment.raw_files_handover_date)}
                </p>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Your Committed Delivery Date *</Label>
                <Input
                  type="date"
                  value={committedDate}
                  onChange={(e) => setCommittedDate(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  data-testid="committed-date-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Choose the date you can deliver the final output
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirm}
                  className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                  data-testid="submit-confirm-btn"
                >
                  Confirm Assignment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FreelancerPortalPage;
